import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { nanoid } from 'nanoid';
import {
  CtxValidators,
  ResourceCreateFormData,
  ResourceUpdateDynamicFormData,
  allowedTagsOnUsers,
  projectEnv,
} from 'utils';
import { CtxDynamicForm } from '../../_forms/models/dynamic-form.model';
import { DataCacheService } from '../../_services/data-cache.service';
import { CtxStepperComponent } from '../stepper/stepper/stepper.component';
import { CtxAlertComponent } from '../alert/alert.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CtxButtonComponent } from '../button/button.component';
import { CtxCustomerSearchComponent } from '../customer-search/customer-search.component';

import {
  AdminOrganizationService,
  AdminResellerService,
  CtxMetadataFormComponent,
  ResellerOrganizationService,
} from 'ng-ui';
import { CtxStepComponent } from '../stepper/step/step.component';
import { CtxTagInputComponent } from '../../_forms/tag-input/tag-input.component';

@Component({
  selector: 'ctx-user-form',
  standalone: true,
  templateUrl: './user-form.component.html',
  styles: [],
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    CtxButtonComponent,
    MatInputModule,
    CtxAlertComponent,
    CtxMetadataFormComponent,
    MatSelectModule,
    MatCheckboxModule,
    CtxCustomerSearchComponent,
    MatTooltipModule,
    CtxStepperComponent,
    CtxStepComponent,
    CtxTagInputComponent,
  ],
})
/** Used both in admin and customer portal */
export class UserFormComponent extends CtxDynamicForm implements OnInit {
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public dialogData: ResourceCreateFormData | ResourceUpdateDynamicFormData,
    public dialogRef: MatDialogRef<UserFormComponent>,
    fb: FormBuilder,
    public dataCacheService: DataCacheService,
    public adminOrganizationService: AdminOrganizationService,
    public resellerService: AdminResellerService,
    public resellerOrganizationService: ResellerOrganizationService
  ) {
    super(fb);
  }

  organizationService: AdminOrganizationService | ResellerOrganizationService =
    this.resellerOrganizationService;
  /**  */
  @ViewChild(CtxStepperComponent) ctxStepper: CtxStepperComponent;
  project = projectEnv.get('projectName');

  userRoles = ['user', 'organization-admin'];

  METADATA_HINT_TEXT = `User metadata allows you to have miscellaneous data associated to this user. Use the 'Add Metadata' button to add a metadata field.`;
  allowedTagsOnUsers = allowedTagsOnUsers;
  organizations = this.dataCacheService.getCachedValues('organization');
  /**
   * tags are cached in DataCacheService using a resolver.
   */
  tags = this.dataCacheService.getCachedValues('tag');
  /**
   * FormGroup that handles validation on the Details tab. It is initialized OnInit.
   */
  detailsTab: FormControl;
  metadataTab: FormArray;

  /** True if password is visible */
  isPasswordVisible = false;

  userGroups = this.dataCacheService.getCachedValues('user-group');

  ngOnInit(): void {
    if (projectEnv.get('projectName') === 'admin-portal') {
      this.userRoles.push('reseller-admin');
      this.organizationService = this.adminOrganizationService;
    }

    this.formGroup = this.fb.group({
      firstName: [
        this.getInitialControlValue('firstName', this.dialogData),
        Validators.maxLength(256),
      ],
      lastName: [
        this.getInitialControlValue('lastName', this.dialogData),
        Validators.maxLength(256),
      ],
      email: [
        this.getInitialControlValue('email', this.dialogData),
        Validators.email,
      ],
      organizationId: new FormControl(
        {
          value: this.getInitialControlValue(
            'organization.id',
            this.dialogData
          ),
          disabled: this.project === 'customer-portal',
        },

        [Validators.maxLength(256), Validators.required]
      ),
      resellerId: new FormControl(
        {
          value: this.getInitialControlValue('reseller.id', this.dialogData),
          disabled: this.project !== 'admin-portal',
        },

        Validators.maxLength(256)
      ),
      company: new FormControl(
        {
          value: this.getInitialControlValue('company', this.dialogData),
          disabled: this.project === 'customer-portal',
        },

        Validators.maxLength(256)
      ),
      password: new FormControl(
        {
          value: '',
          disabled: this.dialogData.mode === 'Update',
        },
        Validators.minLength(8)
      ),
      tags: new FormControl(
        {
          value: this.getInitialControlValue('tags', this.dialogData),
          disabled: this.project !== 'admin-portal',
        },

        CtxValidators.maxItemsAllowed(allowedTagsOnUsers, 'tags')
      ),
      role: [
        this.getInitialControlValue('role', this.dialogData) || 'user',
        [Validators.required],
      ],
      allowCustomerPortalAccess: new FormControl({
        value: this.getInitialControlValue(
          'allowCustomerPortalAccess',
          this.dialogData,
          true
        ),
        disabled:
          this.dialogData.mode !== 'Create' &&
          this.project === 'customer-portal',
      }),
      userGroupIds: new FormControl(
        {
          value: this.getInitialControlValue(
            'userGroups',
            this.dialogData
          )?.map((userGroup: any) => {
            return userGroup.id;
          }),
          disabled: this.project !== 'customer-portal',
        },
        Validators.required
      ),
      metadata: this.fb.array(
        this.getInitialFormArrayValue('metadata', this.dialogData),

        [CtxValidators.uniqueKeys('key')]
      ),
    });
    if (this.project !== 'admin-portal') {
      //disbaling fbArray for form not used in admin-portal
      this.formGroup.value.metadata?.controls?.forEach(
        (control: FormControl) => {
          {
            control.disable();
          }
        }
      );
    }
    this.toggleRequiredValidators(this.formGroup.get('role')?.value);
    /**
     * Create a separate group to validate tabs.
     */
    this.detailsTab = this.fb.control([
      this.formGroup.get('firstName'),
      this.formGroup.get('lastName'),
      this.formGroup.get('email'),
      this.formGroup.get('company'),
      this.formGroup.get('password'),
      this.formGroup.get('organizationId'),
      this.formGroup.get('resellerId'),
      this.formGroup.get('tags'),
      this.formGroup.get('role'),
      this.formGroup.get('allowCustomerPortalAccess'),
    ]);
    /**
     * Assign `metadata` control from the formGroup to metadataFields property
     */
    this.metadataTab = this.formGroup.get('metadata') as FormArray;
  }

  /**
   * Auto-generated password string if event.value === 'auto', else enter input manually
   */
  handlePasswordCheckbox() {
    this.formGroup.patchValue({ password: nanoid() });
  }

  _submit() {
    if (this.ctxStepper) {
      this.submit(
        this.formGroup.value,
        this.dialogData,
        this.dialogRef,
        this.ctxStepper
      );
    } else {
      this.submit(this.formGroup.value, this.dialogData, this.dialogRef);
    }
  }

  /**
   * Handles the change event of the role select dropdown
   * @param event
   */
  handleRoleChange(event: MatSelectChange) {
    this.toggleRequiredValidators(event.value);
    switch (event.value) {
      case 'organization-admin':
        this.formGroup.get('resellerId')?.reset();
        break;
      case 'reseller-admin':
        this.formGroup.get('organizationId')?.reset();
        break;
      default:
        this.formGroup.get('resellerId')?.reset();
        this.formGroup.get('organizationId')?.reset();
    }
  }

  /**
   * Toggles the required validators for the organizationId and resellerId fields is case role is other than user
   * @param value
   */
  toggleRequiredValidators(value: string) {
    const orgValidators =
      value === 'organization-admin' ? [Validators.required] : [];
    const resValidators =
      value === 'reseller-admin' ? [Validators.required] : [];

    this.formGroup.get('organizationId')?.setValidators(orgValidators);
    this.formGroup.get('resellerId')?.setValidators(resValidators);

    this.formGroup.get('organizationId')?.updateValueAndValidity();
    this.formGroup.get('resellerId')?.updateValueAndValidity();
  }
}
